import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, RefreshCcw, Clock3, XCircle } from 'lucide-react';
import { adminUsersAPI } from '../api';

const AccessRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [processingId, setProcessingId] = useState('');

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminUsersAPI.accessRequests();
      setRequests(response?.data?.requests || []);
    } catch (requestError) {
      setError(requestError.message || 'Failed to load access requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const filteredRequests = useMemo(() => {
    if (filter === 'all') {
      return requests;
    }
    return requests.filter((item) => item.account_type === filter);
  }, [requests, filter]);

  const reviewRequest = async (request, status) => {
    const key = `${request.account_type}:${request.id}`;
    setProcessingId(key);

    try {
      await adminUsersAPI.reviewAccessRequest(request.account_type, request.id, status);
      setRequests((prev) => prev.filter((item) => !(item.account_type === request.account_type && item.id === request.id)));
    } catch (requestError) {
      window.alert(requestError.message || 'Failed to update request');
    } finally {
      setProcessingId('');
    }
  };

  const accountTypeBadge = (type) => {
    if (type === 'admin') {
      return 'text-red-300 bg-red-900/30 border border-red-700/40';
    }
    return 'text-blue-300 bg-blue-900/30 border border-blue-700/40';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-100">Access Requests</h2>
          <p className="text-slate-400 mt-2">Approve or reject pending client and admin signups</p>
        </div>
        <button
          onClick={fetchRequests}
          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg transition-colors flex items-center"
        >
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      <div className="admin-glass rounded-xl p-5 border border-slate-700/50 flex flex-wrap gap-3">
        {[
          { key: 'all', label: 'All Pending' },
          { key: 'client', label: 'Client Requests' },
          { key: 'admin', label: 'Admin Requests' }
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => setFilter(item.key)}
            className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
              filter === item.key
                ? 'border-red-600/60 bg-red-600/20 text-red-300'
                : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6">
          <h3 className="text-xl font-bold text-slate-100 mb-4">Pending Requests ({filteredRequests.length})</h3>

          {loading ? (
            <div className="h-64 flex items-center justify-center text-slate-400">Loading pending requests...</div>
          ) : error ? (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <XCircle className="w-12 h-12 mx-auto mb-2 text-red-400" />
                <p className="text-red-300">{error}</p>
              </div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="h-64 flex items-center justify-center border border-dashed border-slate-700 rounded-lg">
              <div className="text-center text-slate-400">
                <Clock3 className="w-10 h-10 mx-auto mb-2" />
                No pending requests in this filter.
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Name</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Type</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Submitted</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((request) => {
                    const key = `${request.account_type}:${request.id}`;
                    const isProcessing = processingId === key;

                    return (
                      <tr key={key} className="border-b border-slate-800 hover:bg-slate-800/20">
                        <td className="py-4 px-4 text-slate-100 font-medium">{request.name}</td>
                        <td className="py-4 px-4 text-slate-300">{request.email}</td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${accountTypeBadge(request.account_type)}`}>
                            {request.account_type}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-slate-400 text-sm">
                          {request.created_at ? new Date(request.created_at).toLocaleString() : '-'}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => reviewRequest(request, 'approved')}
                              disabled={isProcessing}
                              className="px-3 py-2 rounded-lg bg-emerald-700/20 border border-emerald-600/40 text-emerald-300 hover:bg-emerald-700/30 disabled:opacity-60 flex items-center text-sm"
                            >
                              <ShieldCheck className="w-4 h-4 mr-1" />
                              Approve
                            </button>
                            <button
                              onClick={() => reviewRequest(request, 'rejected')}
                              disabled={isProcessing}
                              className="px-3 py-2 rounded-lg bg-red-800/20 border border-red-700/40 text-red-300 hover:bg-red-800/30 disabled:opacity-60 flex items-center text-sm"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessRequests;
